import { useDataGridConfig } from '@/hooks/useDataGridConfig'
import http from '@/lib/http'
import { calculateAgeFromDobString, parseSummaryCareRecord, type ParsedSCRData } from '@/lib/utils'
import { keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { GridFilterItem, GridSortModel } from '@mui/x-data-grid'
import { buildFilterParams, type FilterFieldMapping, type SortFieldMapping } from '@/lib/dataGridFilters'
import { enqueueSnackbar } from 'notistack'

// GridSortItem is not exported from @mui/x-data-grid, so we extract it from GridSortModel
type GridSortItem = GridSortModel[number]

/**
 * Field mappings for patient DataGrid filters.
 * Maps DataGrid column fields to backend filter parameter names.
 */
export const PATIENT_FILTER_FIELD_MAPPING: FilterFieldMapping = {
  firstName: { paramName: 'firstNameFilter', type: 'text' },
  lastName: { paramName: 'lastNameFilter', type: 'text' },
  email: { paramName: 'emailFilter', type: 'text' },
  gender: { paramName: 'genderFilter', type: 'text' },
  phone: { paramName: 'phoneFilter', type: 'text' },
  address1: { paramName: 'address1Filter', type: 'text' },
  address2: { paramName: 'address2Filter', type: 'text' },
  postcode: { paramName: 'postcodeFilter', type: 'text' },
  dateOfBirth: { paramName: 'dateOfBirthFilter', type: 'date' },
  lastContact: { paramName: 'createdAtFilter', type: 'date' },
  idVerified: { paramName: 'idVerifiedFilter', type: 'boolean' },
  isDuplicate: { paramName: 'isDuplicateFilter', type: 'boolean' }
}

export const PATIENT_EXCLUDED_FILTERS_FROM_POPOVER_VIEW = ['isDuplicate']

/**
 * Field mappings for patient DataGrid sorting.
 * Maps DataGrid column fields to backend sort field names.
 */
export const PATIENT_SORT_FIELD_MAPPING: SortFieldMapping = {
  firstName: 'first_name',
  lastName: 'last_name',
  gender: 'gender',
  dateOfBirth: 'date_of_birth',
  lastContact: 'created_at'
}

export type FetchPatientsParams = {
  searchQuery?: string
  searchMode?: 'contains' | 'startsWith'
  searchAttributes?: string
  /** When true, skip the total COUNT query. Response `count` reflects only returned rows. */
  skipCount?: boolean
  skip?: number
  limit?: number

  // Advanced filters (JSON-encoded)
  // patientFilter matches a single token against first OR last name; retained for back-compat.
  // Name search now uses the per-field firstNameFilter/lastNameFilter (combinable for "first last").
  patientFilter?: string
  firstNameFilter?: string
  lastNameFilter?: string
  emailFilter?: string
  genderFilter?: string
  phoneFilter?: string
  // Address filters match against any of the patient's addresses
  address1Filter?: string
  address2Filter?: string
  postcodeFilter?: string
  dateOfBirthFilter?: string
  createdAtFilter?: string

  // Boolean filters (not JSON-encoded)
  idVerifiedFilter?: string

  // Sorting
  sortBy?: 'first_name' | 'last_name' | 'gender' | 'date_of_birth' | 'created_at'
  sortByDirection?: 'asc' | 'desc'
}

export interface PatientListItem {
  id: number | string
  firstName: string
  lastName: string
  gender: string
  dateOfBirth: string | null
  postcode: string | null
  lastContact: string
  idVerified?: boolean
}

export type FetchPatientsResponse = {
  patients: PatientListItem[]
  count: number
}

// API response type (snake_case from backend)
type APIPatient = {
  hash: string
  patient_key: string
  first_name: string
  last_name: string
  date_of_birth: string | null
  gender: string | null
  nhs_number: string
  mobile: string
  user_email: string
  id_provided: string | null
  id_verified: string
  mrz_code: string | null
  summary_care_record: string | null
  summary_care_record_updated_at: string | null
  is_duplicate: string
  created_at: string | null
  // 1:1 patient_address projection
  address_postal_code: string | null
  address_street: string | null
  address_note: string | null
}

type APIResponse = {
  patients: APIPatient[]
  count: number
}

/**
 * Transform API patient (snake_case) to frontend PatientListItem (camelCase)
 */
function toPatientListItem(apiPatient: APIPatient): PatientListItem {
  return {
    id: apiPatient.hash,
    firstName: apiPatient.first_name,
    lastName: apiPatient.last_name,
    gender: apiPatient.gender ?? '',
    dateOfBirth: apiPatient.date_of_birth,
    postcode: apiPatient.address_postal_code,
    lastContact: apiPatient.created_at || '',
    idVerified: apiPatient.id_verified === '1'
  }
}

/**
 * Fetch patients with optional search, filters, and pagination
 * @returns Paginated list of patients with total count
 */
export const fetchPatients = async (
  params: FetchPatientsParams = {},
  signal?: AbortSignal
): Promise<FetchPatientsResponse> => {
  const { patients, count } = (await http.get('/patients', {
    params,
    signal
  })) as APIResponse

  return {
    patients: patients.map(toPatientListItem),
    count: count
  }
}

export type UsePatientsQueryOptions = {
  tableId?: string
  enabled?: boolean
  /** When provided, bypasses DataGrid config and uses these params directly (for compact/search mode). */
  params?: Pick<
    FetchPatientsParams,
    | 'searchQuery'
    | 'searchMode'
    | 'limit'
    | 'skip'
    | 'searchAttributes'
    | 'emailFilter'
    | 'firstNameFilter'
    | 'lastNameFilter'
    | 'phoneFilter'
    | 'address1Filter'
    | 'address2Filter'
    | 'postcodeFilter'
    | 'skipCount'
  >
}

/**
 * Hook to fetch patients with DataGrid integration.
 * Supports search query, column filters, sorting, and pagination.
 *
 * When `params` is provided, bypasses DataGrid config (pagination, filters, sorting)
 * and queries directly with the given params — useful for dashboard search.
 */
export const usePatientsQuery = ({
  tableId = 'patients',
  enabled = true,
  params: overrideParams
}: UsePatientsQueryOptions = {}) => {
  const dataGridConfig = useDataGridConfig({
    tableId,
    syncToUrl: !overrideParams
  })

  const queryParams: FetchPatientsParams = useMemo(() => {
    if (overrideParams) return overrideParams

    const { paginationModel, filters, filterModel, sortModel } = dataGridConfig

    const params: FetchPatientsParams = {
      skip: paginationModel ? paginationModel.page * paginationModel.pageSize : 0,
      limit: paginationModel?.pageSize ?? 10,
      // Table view: skip the unbounded COUNT(*) (slow over ~860k rows). The grid's row
      // count is derived from the page itself and capped at PATIENTS_TABLE_ROW_CAP below.
      skipCount: true
    }

    const filterParams = buildFilterParams({
      searchQuery: filterModel?.quickFilterValues?.join(' '),
      filterItems: filters as GridFilterItem[],
      sortItems: (sortModel ?? []) as GridSortItem[],
      filterFieldMapping: PATIENT_FILTER_FIELD_MAPPING,
      sortFieldMapping: PATIENT_SORT_FIELD_MAPPING
    })

    return { ...params, ...filterParams }
  }, [overrideParams, dataGridConfig])

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['patients', queryParams],
    queryFn: ({ signal }) => fetchPatients(queryParams, signal),
    enabled,
    placeholderData: keepPreviousData
  })

  const patients = useMemo(
    () =>
      (data?.patients ?? []).map(patient => ({
        ...patient,
        dateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth) : null,
        lastContact: patient.lastContact ? new Date(patient.lastContact) : null
      })),
    [data?.patients]
  )

  // Table view skips the COUNT query, so derive the grid's row count from the page:
  // while full pages keep arriving and we're under the cap, the total is unknown
  // (-1 → MUI renders "more than N" and keeps Next enabled)
  const tablePagination = useMemo(() => {
    const limit = queryParams.limit ?? 10
    const skip = queryParams.skip ?? 0
    const hasNextPage = patients.length === limit
    return { hasNextPage, rowCount: hasNextPage ? -1 : skip + patients.length }
  }, [queryParams.limit, queryParams.skip, patients.length])

  // Override path (dashboard/ringcentral search) keeps its existing count semantics;
  // the table view uses the derived, capped row count above.
  const overrideCount = queryParams.skipCount ? patients.length : (data?.count ?? 0)
  const count = overrideParams ? overrideCount : tablePagination.rowCount

  return {
    patients,
    count,
    hasNextPage: tablePagination.hasNextPage,
    isLoading,
    isFetching,
    isError,
    error,
    dataGridConfig
  }
}

export type APIPatientAddress = {
  type: string | null
  street: string | null
  city: string | null
  postal_code: string | null
  county: string | null
  country: string | null
  note: string | null
  created_at: string | null
}

export type APIPatientSupport = {
  contact_type: string | null
  red_flag: string | null
  red_flag_reason: string | null
  // PHP names the column `unhappy` (query API: patient_support.unhappy).
  unhappy?: string | null
  alerts: string | null
  safeguarding_questions: string | null
  created_at: string | null
}

export type APIPatientDetails = {
  hash: string
  linked_patient_hash: string | null
  user_hash: string
  patient_key: string
  first_name: string
  last_name: string
  date_of_birth: string
  gender: string | null
  nhs_number: string
  mobile: string
  user_email: string
  id_provided: string | null
  id_verified: string
  mrz_code: string | null
  summary_care_record: string | null
  summary_care_record_updated_at: string | null
  is_duplicate: string
  created_at: string | null
  addresses: APIPatientAddress[]
  support: APIPatientSupport[]
  icu_check: boolean
  medications: string | null
  allergies: string | null
  social_history: string | null
  family_history: string | null
  conditions: string | null
  other_observations: string | null
  id_other: string | null
  gp: { hash: string; name: string } | null
  user_details: {
    first_name: string
    last_name: string
    email: string
    mobile: string | null
  }
}

export interface PatientUserDetails {
  firstName: string
  lastName: string
  email: string
  mobile: string | null
}

export interface PatientAddress {
  type: string | null
  street: string | null
  city: string | null
  postalCode: string | null
  county: string | null
  country: string | null
  note: string | null
  createdAt: string | null
}

export interface PatientSupport {
  contactType: string | null
  redFlag: string | null
  redFlagReason: string | null
  unhappyCustomer: boolean
  alerts: string | null
  safeguardingQuestions: string | null
  createdAt: string | null
}

export interface PatientDetails {
  hash: string
  linkedPatientHash: string | null
  userHash: string
  firstName: string
  lastName: string
  gender: string
  userEmail: string
  userDetails: PatientUserDetails
  mobile?: string
  phone?: string
  dob: string
  age: number
  alerts?: string
  idDocument?: string
  redFlag: string
  hasRedFlag: boolean
  redFlagReason?: string
  unhappyCustomer: boolean
  icuCheck?: boolean
  idVerified?: boolean
  medications?: string
  allergies?: string
  familyHistory?: string
  conditions?: string
  socialHistory?: string
  otherObservations?: string
  summaryCareRecord: ParsedSCRData | null
  summaryCareRecordLastUpdated: Date | null
  addresses: PatientAddress[]
  primaryAddress: PatientAddress | null
  support: PatientSupport[]
  idOther: string | null
  idProvided: string | null
  isDuplicate: boolean
  gp: { hash: string; name: string } | null
}

// Converts API patient address to frontend patient address
function toPatientAddress(api: APIPatientAddress): PatientAddress {
  return {
    type: api.type,
    street: api.street,
    city: api.city,
    postalCode: api.postal_code,
    county: api.county,
    country: api.country,
    note: api.note,
    createdAt: api.created_at
  }
}

// The PHP API reads & writes a patient's postal code on the address returned by
// ep_patient::get_address() — the latest 'shipping' address (ORDER BY created_at
// DESC). The detail query returns addresses oldest-first, so reading addresses[0]
// surfaces a different (older) address and makes postal-code edits look like they
// don't save. Mirror the backend so the address we read is the one it writes to.
function getPrimaryAddress(addresses: PatientAddress[]): PatientAddress | null {
  const shipping = addresses.filter(address => address.type === 'shipping')
  const pool = shipping.length > 0 ? shipping : addresses
  return (
    pool.slice().sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())[0] ?? null
  )
}

// patient_support TINYINT flags serialise as "1" / "0", matching the hasRedFlag check below
const isSupportFlagSet = (value: string | null | undefined): boolean => value === '1' || value === 'true'

// Converts API patient support to frontend patient support
function toPatientSupport(api: APIPatientSupport): PatientSupport {
  return {
    contactType: api.contact_type,
    redFlag: api.red_flag,
    redFlagReason: api.red_flag_reason,
    unhappyCustomer: isSupportFlagSet(api.unhappy),
    alerts: api.alerts,
    safeguardingQuestions: api.safeguarding_questions,
    createdAt: api.created_at
  }
}

// Converts API patient details to frontend patient details
function toPatientDetails(api: APIPatientDetails): PatientDetails {
  // Find the most recent support entry for red flag info
  const latestSupport = api.support.sort(
    (a, b) => new Date(b.created_at ?? '').getTime() - new Date(a.created_at ?? '').getTime()
  )[0]

  const addresses = api.addresses.map(toPatientAddress)

  return {
    hash: api.hash,
    linkedPatientHash: api.linked_patient_hash,
    userHash: api.user_hash,
    firstName: api.first_name,
    lastName: api.last_name,
    gender: api.gender ?? '',
    userEmail: api.user_email,
    userDetails: {
      firstName: api.user_details.first_name,
      lastName: api.user_details.last_name,
      email: api.user_details.email,
      mobile: api.user_details.mobile
    },
    mobile: api.mobile,
    dob: api.date_of_birth,
    age: calculateAgeFromDobString(api.date_of_birth),
    alerts: latestSupport?.alerts ?? undefined,
    idDocument: api.id_provided ?? undefined,
    idProvided: api.id_provided,
    idOther: api.id_other,
    redFlag: latestSupport?.red_flag ?? '',
    hasRedFlag: latestSupport?.red_flag === '1' || latestSupport?.red_flag === 'true',
    redFlagReason: latestSupport?.red_flag_reason ?? undefined,
    unhappyCustomer: isSupportFlagSet(latestSupport?.unhappy),
    icuCheck: api.icu_check,
    idVerified: api.id_verified === '1',
    isDuplicate: api.is_duplicate === '1',
    medications: api.medications ?? '',
    allergies: api.allergies ?? '',
    familyHistory: api.family_history ?? '',
    conditions: api.conditions ?? '',
    socialHistory: api.social_history ?? '',
    otherObservations: api.other_observations ?? '',
    summaryCareRecord: parseSummaryCareRecord(api.summary_care_record, api.summary_care_record_updated_at),
    summaryCareRecordLastUpdated: api.summary_care_record_updated_at
      ? new Date(api.summary_care_record_updated_at)
      : null,
    addresses,
    primaryAddress: getPrimaryAddress(addresses),
    support: api.support.map(toPatientSupport),
    gp: api.gp
  }
}

// Fetches patient details by hash
export const fetchPatientByHash = async (hash: string): Promise<PatientDetails> => {
  const patient = (await http.get(`/patients/${hash}`)) as APIPatientDetails
  return toPatientDetails(patient)
}

// Hook to fetch patient details by hash
export const usePatientByHashQuery = (hash: string) => {
  return useSuspenseQuery({
    queryKey: ['patient', hash],
    queryFn: () => fetchPatientByHash(hash)
  })
}

export type UpdatePatientBody = {
  first_name?: string
  last_name?: string
  date_of_birth?: string
  gender?: 'male' | 'female'
  street?: string
  address_note?: string
  city?: string
  postal_code?: string
  id_verified?: boolean
  id_provided?: string
  id_other?: string
  red_flag?: boolean
  red_flag_reason?: string
  unhappy?: boolean
  alerts?: string
  other?: string
  gp_hash?: string
}

type UpdatePatientResponse = {
  code: string
  message: string
  data?: { patient: string }
}

export const useUpdatePatientDetails = (hash: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: UpdatePatientBody) =>
      http.post(`/patients/${hash}/update-details`, body) as Promise<UpdatePatientResponse>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', hash] })
    }
  })
}

export const useVerifyPatientId = (hash: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userHash: string) =>
      http.patch(`/patients/idu-check/${hash}`, {
        userHash
      }) as Promise<{
        success: boolean
        patientId: string
        workflowId: string
        conversationId: string
        message: string
      }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', hash] })
    }
  })
}

interface FetchSCRParams {
  first_name: string
  last_name: string
  postcode: string
  date_of_birth: string
  id: string
  service: string
  environment: string
}

export const useFetchSCR = (patientHash: string) => {
  return useMutation({
    mutationKey: ['fetchSCR', patientHash],
    mutationFn: (data: FetchSCRParams) => http.post('/patients/scr', data),
    onSuccess: () => {
      enqueueSnackbar('SCR fetch initiated successfully. It may take a few moments for the data to update.', {
        variant: 'success'
      })
    }
  })
}
